import { c as _c } from "react/compiler-runtime";
import { CurrencyEnum, FormInput, FormInputSearch, FormSelect, Popover, Tooltip } from '@systemeio/ui';
import { CheckCircleIcon, QuestionMarkIcon, TimeCircleIcon } from '@systemeio/ui/icons';
import { FUNNELS_API, activateFunnel, createFunnel, removeFunnel, updateFunnel } from 'modules/funnels/api/funnels-api';
import FunnelDuplicateModal from 'modules/funnels/components/funnel-duplicate-modal';
import FunnelGoalSelector from 'modules/funnels/components/funnel-goal-selector';
import { FunnelsLoco } from 'modules/funnels/constants/funnels-loco';
import { useFunnel } from 'modules/funnels/funnel/hooks/use-funnel';
import { useFunnels } from 'modules/funnels/hooks/use-funnels';
import { FunnelFilterInterface, StatusDataInterface } from 'modules/funnels/types/funnel-filter-interface';
import { FunnelAdditionalEditableInterface, FunnelCreateInterface, FunnelEditableInterface, FunnelErrorsInterface, FunnelInterface } from 'modules/funnels/types/funnel-interface';
import { getFunnelUrl } from 'modules/funnels/utils/get-funnel-url';
import { isFunnelCreated } from 'modules/funnels/utils/is-funnel-created';
import { usePaymentSettings } from 'modules/profile/payment/hooks/use-payment-settings';
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum';
import { getShareUrl } from 'modules/share/utils/get-share-url';
import { NextPage } from 'next';
import { useEffect, useState } from 'react';
import { CreateDataType } from 'shared/components/create-modal';
import LinkShareModal from 'shared/components/link-share-modal';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import TransLoco from 'shared/components/trans-loco';
import { removeFunnelStepInDB } from 'shared/db/utils/funnels';
import { BooleanStatusEnum } from 'shared/enums/status-enum';
import { useCurrencyData } from 'shared/hooks/use-currency-data';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import useUserDomains from 'shared/hooks/use-user-domains';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getDomainInfoLink } from 'shared/utils/get-domain-info-link';
import { usePaymentMethodsByCurrency } from '../../shared/hooks/use-payment-methods-by-currency';
import { allowedPaymentMethodsFunnels } from 'modules/funnels/funnel/constants/allowed-payment-methods';
const defaultErrors: FunnelErrorsInterface = {
  name: '',
  domain: '',
  currency: '',
  type: '',
  paymentMethods: ''
};
const defaultFilters: FunnelFilterInterface = {
  search: '',
  status: 'true'
};
const Funnels: NextPage = () => {
  const $ = _c(204);
  const {
    user,
    isWsAssistant
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const {
    userDomains,
    userDomainsSelect,
    userDefaultDomain
  } = useUserDomains();
  const {
    filter,
    setFilter,
    pagination,
    setPagination,
    limit,
    setLimit
  } = useFilterPagination(defaultFilters);
  const {
    paymentSettingsData
  } = usePaymentSettings(!isWsAssistant);
  const [errors, setErrors] = useState(defaultErrors);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      name: "",
      currency: undefined,
      domain: undefined,
      type: undefined
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [defaultValues, setDefaultValues] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      paymentMethods: undefined,
      domain: undefined,
      currency: undefined
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [additionalData, setAdditionalData] = useState(t1);
  let t2;
  if ($[2] !== filter || $[3] !== limit || $[4] !== pagination) {
    t2 = {
      limit,
      pagination,
      filter
    };
    $[2] = filter;
    $[3] = limit;
    $[4] = pagination;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    funnels,
    mutate,
    counter
  } = useFunnels(t2);
  const [funnelId, setFunnelId] = useState();
  const {
    funnel,
    mutateFunnel
  } = useFunnel(funnelId);
  const t3 = funnel?.currency;
  let t4;
  if ($[6] !== t) {
    t4 = t("dashboard.funnel.currency_change_info");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== errors || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      currency: t3,
      currencyChangeMessage: t4,
      errors,
      allowedPaymentMethods: allowedPaymentMethodsFunnels
    };
    $[8] = errors;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    currencyAndPaymentMethodsEditFields
  } = usePaymentMethodsByCurrency(t5);
  let t6;
  if ($[12] !== funnels?.items) {
    t6 = () => {
      const array = funnels?.items.map(_temp);
      return array || [];
    };
    $[12] = funnels?.items;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const getStaticFunnelItems = t6;
  let t7;
  if ($[14] !== getStaticFunnelItems) {
    t7 = [`${FUNNELS_API}/get-payment-methods`, ...getStaticFunnelItems()];
    $[14] = getStaticFunnelItems;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== setPagination || $[17] !== t7) {
    t8 = {
      setPagination,
      api: FUNNELS_API,
      constantApi: t7
    };
    $[16] = setPagination;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const updateData = useUpdateData(t8);
  let t9;
  if ($[19] !== funnels || $[20] !== updateData) {
    t9 = async () => {
      await updateData(false, funnels);
    };
    $[19] = funnels;
    $[20] = updateData;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const onUpdateData = t9;
  const currencyData = useCurrencyData();
  let t10;
  if ($[22] !== paymentSettingsData || $[23] !== userDefaultDomain) {
    t10 = () => {
      if (userDefaultDomain) {
        setDefaultValues(prev => ({
          ...prev,
          domain: userDefaultDomain.id
        }));
      }
      if (paymentSettingsData) {
        setDefaultValues(prev_0 => ({
          ...prev_0,
          currency: paymentSettingsData.defaultCurrency
        }));
      }
    };
    $[22] = paymentSettingsData;
    $[23] = userDefaultDomain;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== paymentSettingsData || $[26] !== userDefaultDomain || $[27] !== userDomains) {
    t11 = [userDomains, paymentSettingsData, userDefaultDomain];
    $[25] = paymentSettingsData;
    $[26] = userDefaultDomain;
    $[27] = userDomains;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[29] !== funnel) {
    t12 = () => {
      if (funnel) {
        setAdditionalData({
          paymentMethods: funnel.paymentMethods,
          currency: funnel.currency,
          domain: funnel.domain.id
        });
      }
    };
    t13 = [funnel];
    $[29] = funnel;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  let t14;
  if ($[32] !== t) {
    t14 = () => Object.keys(BooleanStatusEnum).map(key => {
      const status = key as keyof typeof BooleanStatusEnum;
      return {
        id: status,
        caption: t(BooleanStatusEnum[status])
      };
    });
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const getStatusData = t14;
  let t15;
  if ($[34] !== t) {
    t15 = t("global.title");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = {
      key: "name",
      header: t15,
      widthPercent: 73,
      onRender: _temp2
    };
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("global.status");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = funnel_1 => {
      const StatusOfCreatedFunnel = <Tooltip label={funnel_1.active ? t("global.active") : t("global.inactive")} mode="hover" key="status-tooltip"><CheckCircleIcon className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${funnel_1.active && "fill-green"}`} /></Tooltip>;
      const StatusOfNotCreatedFunnel = <Tooltip label={t("dashboard.funnel.creation_in_process")} mode="hover" key="status-tooltip"><TimeCircleIcon className="fill-yellow h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]" /></Tooltip>;
      return <div className="flex items-center lg:ml-4">{isFunnelCreated(funnel_1) ? StatusOfCreatedFunnel : StatusOfNotCreatedFunnel}</div>;
    };
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t17 || $[43] !== t18) {
    t19 = {
      key: "status",
      header: t17,
      widthPercent: 10,
      onRender: t18
    };
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t("global.created_on");
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== user?.dateFormat || $[48] !== user?.timeFormat || $[49] !== user?.timezone) {
    t21 = funnel_2 => <span>{dateTimeFormat(funnel_2.createdAt, {
        timeFormat: user?.timeFormat,
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      })}</span>;
    $[47] = user?.dateFormat;
    $[48] = user?.timeFormat;
    $[49] = user?.timezone;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t20 || $[52] !== t21) {
    t22 = {
      key: "funnel.createdAt",
      header: t20,
      widthPercent: 17,
      onRender: t21
    };
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t16 || $[55] !== t19 || $[56] !== t22) {
    t23 = [t16, t19, t22];
    $[54] = t16;
    $[55] = t19;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const columns = t23;
  let t24;
  if ($[58] !== defaultValues || $[59] !== funnels || $[60] !== updateData) {
    t24 = async data => {
      await fetchWithErrors(async () => {
        const newData = {
          ...data,
          currency: data.currency || defaultValues.currency || CurrencyEnum.EURO
        };
        await createFunnel(newData);
        await updateData(true, funnels);
      }, setErrors);
    };
    $[58] = defaultValues;
    $[59] = funnels;
    $[60] = updateData;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const onCreateFunnel = t24;
  let t25;
  if ($[62] !== funnels || $[63] !== mutate || $[64] !== mutateFunnel || $[65] !== updateData) {
    t25 = async (funnel_3, data_0) => {
      await fetchWithErrors(async () => {
        const newData_0 = {
          name: data_0.name,
          paymentMethods: data_0.paymentMethods,
          domain: data_0.domain,
          currency: data_0.currency
        };
        const response = await updateFunnel(funnel_3.id, newData_0);
        await updateData(false, undefined, {
          mutationData: funnels,
          onMutate: async () => {
            await mutate(data_1 => {
              if (data_1) {
                return {
                  items: data_1.items.map(item => {
                    if (item.id === funnel_3.id) {
                      item = {
                        ...item,
                        name: response.data.name
                      };
                    }
                    return item;
                  }),
                  hasMore: data_1.hasMore
                };
              }
            }, false);
          }
        });
        await mutateFunnel(data_2 => data_2 && {
          ...data_2,
          ...response.data
        }, false);
      }, setErrors);
    };
    $[62] = funnels;
    $[63] = mutate;
    $[64] = mutateFunnel;
    $[65] = updateData;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  const onUpdateFunnel = t25;
  let t26;
  if ($[67] !== funnels || $[68] !== mutate || $[69] !== updateData) {
    t26 = async funnel_4 => {
      await activateFunnel(funnel_4.id, funnel_4.active);
      await updateData(false, undefined, {
        mutationData: funnels,
        onMutate: async () => {
          await mutate(data_3 => {
            if (data_3) {
              return {
                items: data_3.items.map(item_0 => {
                  if (item_0.id === funnel_4.id) {
                    item_0 = {
                      ...item_0,
                      active: !funnel_4.active
                    };
                  }
                  return item_0;
                }),
                hasMore: data_3.hasMore
              };
            }
          }, false);
        }
      });
    };
    $[67] = funnels;
    $[68] = mutate;
    $[69] = updateData;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  const onActivateFunnel = t26;
  let t27;
  if ($[71] !== onUpdateData) {
    t27 = async funnel_5 => {
      await removeFunnel(funnel_5.id);
      await removeFunnelStepInDB(funnel_5.id);
      await onUpdateData();
    };
    $[71] = onUpdateData;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  const onRemoveFunnel = t27;
  let t28;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setErrors(defaultErrors);
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const onModalClose = t28;
  let t29;
  if ($[74] !== t) {
    t29 = t("dashboard.funnel.title_plural");
    $[74] = t;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t) {
    t30 = t("dashboard.funnel.title_tooltip");
    $[76] = t;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t30) {
    t31 = <Popover label={t30} popoverClassName="w-[200px] sm:w-[350px]">{_temp3}</Popover>;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t29 || $[81] !== t31) {
    t32 = <h1 className="flex items-center gap-1.5">{t29}{t31}</h1>;
    $[80] = t29;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== filter || $[84] !== setFilter) {
    t33 = {
      filter,
      setFilter,
      defaultFilters
    };
    $[83] = filter;
    $[84] = setFilter;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== pagination || $[87] !== setPagination) {
    t34 = {
      pagination,
      setPagination
    };
    $[86] = pagination;
    $[87] = setPagination;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== limit || $[90] !== setLimit) {
    t35 = {
      limit,
      setLimit
    };
    $[89] = limit;
    $[90] = setLimit;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      field: "search",
      onRender: _temp4
    };
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== getStatusData) {
    t37 = [t36, {
      field: "status",
      onRender: (value_0, onChange_0) => <FormSelect key="funnel-status-filter" data={getStatusData()} onChange={data_4 => onChange_0(data_4)} value={value_0} />
    }];
    $[93] = getStatusData;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t33 || $[96] !== t34 || $[97] !== t35 || $[98] !== t37) {
    t38 = {
      filterer: t33,
      paginator: t34,
      limiter: t35,
      testAttributePostfix: "filter-btn",
      filterable: t37
    };
    $[95] = t33;
    $[96] = t34;
    $[97] = t35;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== onCreateFunnel) {
    t39 = data_5 => onCreateFunnel(data_5);
    $[100] = onCreateFunnel;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== t) {
    t40 = t("dashboard.funnel.title");
    $[102] = t;
    $[103] = t40;
  } else {
    t40 = $[103];
  }
  let t41;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = ["name", "domain", "type"];
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== errors || $[106] !== t) {
    t42 = {
      field: "name",
      onRender: (value_1, onChange_1) => <FormInput key="funnel-name-create" label={t("global.name")} placeholder={t("global.name")} required={true} value={value_1} onChange={e_0 => onChange_1(e_0.target.value)} maxLength={256} error={errors.name} data-test="funnel-name-field" />
    };
    $[105] = errors;
    $[106] = t;
    $[107] = t42;
  } else {
    t42 = $[107];
  }
  let t43;
  if ($[108] !== errors || $[109] !== t || $[110] !== user?.dashboardLocale || $[111] !== userDomainsSelect) {
    t43 = {
      field: "domain",
      onRender: (value_2, onChange_2) => <FormSelect key="funnel-domain-create" label={<div className="flex items-center gap-1">{t("dashboard.funnel.funnel_domain")}<Popover label={<TransLoco t={t} i18nKey="dashboard.funnel.domain_tooltip-2" components={{
          a: <a rel="noreferrer" target="_blank" className="primary-link w-full" href={getDomainInfoLink(user?.dashboardLocale)} />
        }} />} popoverClassName="w-[200px] sm:w-[350px]">{_temp5}</Popover></div>} required={true} data={userDomainsSelect} onChange={data_6 => onChange_2(data_6)} value={value_2} error={errors.domain} />
    };
    $[108] = errors;
    $[109] = t;
    $[110] = user?.dashboardLocale;
    $[111] = userDomainsSelect;
    $[112] = t43;
  } else {
    t43 = $[112];
  }
  let t44;
  if ($[113] !== t42 || $[114] !== t43) {
    t44 = [t42, t43];
    $[113] = t42;
    $[114] = t43;
    $[115] = t44;
  } else {
    t44 = $[115];
  }
  let t45;
  if ($[116] !== errors) {
    t45 = {
      field: "type",
      onRender: (value_3, onChange_3) => <FunnelGoalSelector key="funnel-type-create" value={value_3} onChange={onChange_3} error={errors.type} />
    };
    $[116] = errors;
    $[117] = t45;
  } else {
    t45 = $[117];
  }
  let t46;
  if ($[118] !== currencyData || $[119] !== errors || $[120] !== t) {
    t46 = {
      field: "currency",
      onRender: (value_4, onChange_4) => <FormSelect key="funnel-currency-create" label={t("global.currency")} data={currencyData} onChange={data_7 => onChange_4(data_7)} value={value_4} error={errors.currency} />
    };
    $[118] = currencyData;
    $[119] = errors;
    $[120] = t;
    $[121] = t46;
  } else {
    t46 = $[121];
  }
  let t47;
  if ($[122] !== t44 || $[123] !== t45 || $[124] !== t46) {
    t47 = [t44, t45, t46];
    $[122] = t44;
    $[123] = t45;
    $[124] = t46;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== defaultValues || $[127] !== t39 || $[128] !== t40 || $[129] !== t47) {
    t48 = {
      onCreate: t39,
      defaultValues,
      caption: t40,
      onAfterLeave: onModalClose,
      requiredFields: t41,
      testAttributePostfix: "create-btn",
      dataTestKey: "funnel",
      creatable: t47
    };
    $[126] = defaultValues;
    $[127] = t39;
    $[128] = t40;
    $[129] = t47;
    $[130] = t48;
  } else {
    t48 = $[130];
  }
  let t49;
  if ($[131] !== t) {
    t49 = t("dashboard.funnel.title");
    $[131] = t;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  if ($[133] !== limit || $[134] !== setLimit) {
    t50 = {
      limit,
      setLimit
    };
    $[133] = limit;
    $[134] = setLimit;
    $[135] = t50;
  } else {
    t50 = $[135];
  }
  let t51;
  if ($[136] !== pagination || $[137] !== setPagination) {
    t51 = {
      pagination,
      setPagination
    };
    $[136] = pagination;
    $[137] = setPagination;
    $[138] = t51;
  } else {
    t51 = $[138];
  }
  let t52;
  let t53;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = ["openLink", "custom", "activate", "settings", "remove"];
    t53 = [{
      shouldRender: _temp6,
      name: "view-funnel",
      openInNew: true,
      setHref: _temp7
    }];
    $[139] = t52;
    $[140] = t53;
  } else {
    t52 = $[139];
    t53 = $[140];
  }
  let t54;
  if ($[141] !== t) {
    t54 = t("dashboard.actions.title.duplicate");
    $[141] = t;
    $[142] = t54;
  } else {
    t54 = $[142];
  }
  let t55;
  if ($[143] !== onUpdateData) {
    t55 = (opened, onClose, funnel_9, key_0) => <FunnelDuplicateModal key={key_0} opened={opened} onClose={() => {
      onModalClose();
      onClose();
    }} funnel={funnel_9} onUpdateData={onUpdateData} />;
    $[143] = onUpdateData;
    $[144] = t55;
  } else {
    t55 = $[144];
  }
  let t56;
  if ($[145] !== t54 || $[146] !== t55) {
    t56 = {
      shouldRender: _temp8,
      name: "duplicate",
      caption: t54,
      onActionRender: t55
    };
    $[145] = t54;
    $[146] = t55;
    $[147] = t56;
  } else {
    t56 = $[147];
  }
  let t57;
  if ($[148] !== t) {
    t57 = t("dashboard.actions.title.share");
    $[148] = t;
    $[149] = t57;
  } else {
    t57 = $[149];
  }
  let t58;
  if ($[150] !== t) {
    t58 = (opened_0, onClose_0, funnel_11, key_1) => <LinkShareModal opened={opened_0} link={getShareUrl(ShareResourceEnum.Funnel, funnel_11.hash)} label={t("dashboard.funnel.share_link")} title={t("dashboard.funnel.share_funnel")} onClose={onClose_0} key={key_1} />;
    $[150] = t;
    $[151] = t58;
  } else {
    t58 = $[151];
  }
  let t59;
  if ($[152] !== t57 || $[153] !== t58) {
    t59 = {
      shouldRender: _temp9,
      name: "share",
      caption: t57,
      onActionRender: t58
    };
    $[152] = t57;
    $[153] = t58;
    $[154] = t59;
  } else {
    t59 = $[154];
  }
  let t60;
  if ($[155] !== t56 || $[156] !== t59) {
    t60 = [t56, t59];
    $[155] = t56;
    $[156] = t59;
    $[157] = t60;
  } else {
    t60 = $[157];
  }
  let t61;
  if ($[158] !== onActivateFunnel) {
    t61 = {
      shouldRender: _temp10,
      onChange: funnel_13 => onActivateFunnel(funnel_13)
    };
    $[158] = onActivateFunnel;
    $[159] = t61;
  } else {
    t61 = $[159];
  }
  let t62;
  if ($[160] !== t) {
    t62 = t("dashboard.funnel.edit_funnel");
    $[160] = t;
    $[161] = t62;
  } else {
    t62 = $[161];
  }
  let t63;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = funnelInstance => {
      setFunnelId(funnelInstance.id);
    };
    $[162] = t63;
  } else {
    t63 = $[162];
  }
  const t64 = !funnel;
  let t65;
  if ($[163] !== errors || $[164] !== t) {
    t65 = {
      field: "name",
      onRender: (value_5, onChange_5, _, __, isPreFetching) => <FormInput isPreFetching={isPreFetching} key="funnel-name-edit" label={t("global.name")} placeholder={t("global.name")} value={value_5} onChange={e_1 => onChange_5(e_1.target.value)} maxLength={256} error={errors.name} />
    };
    $[163] = errors;
    $[164] = t;
    $[165] = t65;
  } else {
    t65 = $[165];
  }
  let t66;
  if ($[166] !== errors || $[167] !== t || $[168] !== user?.dashboardLocale || $[169] !== userDomainsSelect) {
    t66 = [{
      field: "domain",
      onRender: (value_6, onChange_6, __0, ___0, isPreFetching_0) => <FormSelect isPreFetching={isPreFetching_0} key="funnel-domain-edit" label={<div className="flex w-fit items-center gap-1">{t("dashboard.funnel.funnel_domain")}<Popover label={<TransLoco t={t} i18nKey="dashboard.funnel.domain_tooltip-2" components={{
          a: <a rel="noreferrer" target="_blank" className="primary-link w-full" href={getDomainInfoLink(user?.dashboardLocale)} />
        }} />} popoverClassName="w-[200px] sm:w-[350px]">{_temp12}</Popover></div>} data={userDomainsSelect} onChange={data_8 => onChange_6(data_8)} value={value_6} error={errors.domain} withoutCloseIcon={true} />
    }];
    $[166] = errors;
    $[167] = t;
    $[168] = user?.dashboardLocale;
    $[169] = userDomainsSelect;
    $[170] = t66;
  } else {
    t66 = $[170];
  }
  let t67;
  if ($[171] !== currencyAndPaymentMethodsEditFields || $[172] !== t65 || $[173] !== t66) {
    t67 = [t65, t66, ...currencyAndPaymentMethodsEditFields];
    $[171] = currencyAndPaymentMethodsEditFields;
    $[172] = t65;
    $[173] = t66;
    $[174] = t67;
  } else {
    t67 = $[174];
  }
  let t68;
  if ($[175] !== onUpdateFunnel) {
    t68 = (funnel_15, data_9) => onUpdateFunnel(funnel_15, data_9);
    $[175] = onUpdateFunnel;
    $[176] = t68;
  } else {
    t68 = $[176];
  }
  let t69;
  if ($[177] !== additionalData || $[178] !== t62 || $[179] !== t64 || $[180] !== t67 || $[181] !== t68) {
    t69 = {
      shouldRender: _temp11,
      fullCaption: t62,
      onModalOpen: t63,
      isFetching: t64,
      onModalClose,
      additionalData,
      editable: t67,
      onEdit: t68
    };
    $[177] = additionalData;
    $[178] = t62;
    $[179] = t64;
    $[180] = t67;
    $[181] = t68;
    $[182] = t69;
  } else {
    t69 = $[182];
  }
  let t70;
  if ($[183] !== onRemoveFunnel) {
    t70 = {
      shouldRender: _temp13,
      onChange: onRemoveFunnel
    };
    $[183] = onRemoveFunnel;
    $[184] = t70;
  } else {
    t70 = $[184];
  }
  let t71;
  if ($[185] !== t60 || $[186] !== t61 || $[187] !== t69 || $[188] !== t70) {
    t71 = {
      openLink: t53,
      custom: t60,
      activate: t61,
      settings: t69,
      remove: t70
    };
    $[185] = t60;
    $[186] = t61;
    $[187] = t69;
    $[188] = t70;
    $[189] = t71;
  } else {
    t71 = $[189];
  }
  let t72;
  if ($[190] !== columns || $[191] !== funnels || $[192] !== t49 || $[193] !== t50 || $[194] !== t51 || $[195] !== t71) {
    t72 = <div data-test="funnel-list"><Table data={funnels} columns={columns} caption={t49} limiter={t50} paginator={t51} testAttributePostfix="funnel" actionsOrder={t52} actions={t71} /></div>;
    $[190] = columns;
    $[191] = funnels;
    $[192] = t49;
    $[193] = t50;
    $[194] = t51;
    $[195] = t71;
    $[196] = t72;
  } else {
    t72 = $[196];
  }
  let t73;
  if ($[197] !== counter || $[198] !== funnels || $[199] !== t32 || $[200] !== t38 || $[201] !== t48 || $[202] !== t72) {
    t73 = <ListingLayout counter={counter} data={funnels} title={t32} type="compact" filterData={t38} createData={t48}>{t72}</ListingLayout>;
    $[197] = counter;
    $[198] = funnels;
    $[199] = t32;
    $[200] = t38;
    $[201] = t48;
    $[202] = t72;
    $[203] = t73;
  } else {
    t73 = $[203];
  }
  return t73;
};
export const getServerSideProps = getBaseServerSideProps(FunnelsLoco, 'header.top_menu.funnels');
export default Funnels;
function _temp(el) {
  return `${FUNNELS_API}/${el.id}`;
}
function _temp2(funnel_0) {
  return <div className="truncate">{isFunnelCreated(funnel_0) ? <LinkWithoutPrefetch rel="noreferrer" className="primary-link w-full" href={getFunnelUrl(funnel_0.id)}>{funnel_0.name}</LinkWithoutPrefetch> : <span className="w-full">{funnel_0.name}</span>}</div>;
}
function _temp3(show) {
  return <QuestionMarkIcon className={`${show ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary`} />;
}
function _temp4(value, onChange) {
  return <FormInputSearch key="funnel-search-filter" onClear={() => onChange("")} className="min-w-[150px]" value={value} onChange={e => onChange(e.target.value)} testAttributePostfix="search-input" />;
}
function _temp5(show_0) {
  return <QuestionMarkIcon className={`${show_0 ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp6(funnel_6) {
  return isFunnelCreated(funnel_6);
}
function _temp7(funnel_7) {
  return funnel_7.viewLinkUrl;
}
function _temp8(funnel_8) {
  return isFunnelCreated(funnel_8);
}
function _temp9(funnel_10) {
  return isFunnelCreated(funnel_10);
}
function _temp10(funnel_12) {
  return isFunnelCreated(funnel_12);
}
function _temp11(funnel_14) {
  return isFunnelCreated(funnel_14);
}
function _temp12(show_1) {
  return <QuestionMarkIcon className={`${show_1 ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp13(funnel_16) {
  return isFunnelCreated(funnel_16);
}